import React, { useEffect, useRef } from "react";
import "./logoSlider.css";

export default function LogoSlider({
  logos = [],
  speed = 35,
  cardWidth = 200,
  showArrows = true,
}) {
  const trackRef = useRef(null);
  const viewportRef = useRef(null);

  // On triple la liste pour avoir assez de matière pour le scroll infini
  const doubled = [...logos, ...logos, ...logos]; 

  const scrollByCards = (dir = 1) => {
    const el = trackRef.current;
    if (!el) return;
    const delta = dir * (cardWidth + 40);
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  useEffect(() => {
    const track = trackRef.current;
    const viewport = viewportRef.current;
    if (!track || !viewport) return;

    let raf = 0;

    const tick = () => {
      const vpRect = viewport.getBoundingClientRect();
      const centerX = vpRect.left + vpRect.width / 2;

      const cards = Array.from(track.querySelectorAll(".ls__card"));
      const inners = Array.from(track.querySelectorAll(".ls__inner"));

      cards.forEach((card, idx) => {
        const inner = inners[idx];
        const r = card.getBoundingClientRect();
        const cardCenter = r.left + r.width / 2;
        
        // Distance en pixels par rapport au centre absolu
        const dist = Math.abs(cardCenter - centerX);
        
        // --- LOGIQUE DE ZONES ---
        // 1. Zone Centre : dist très faible (< 60% d'une carte)
        const isCenter = dist < (cardWidth * 0.6);
        
        // 2. Zone Voisin : dist comprise entre le centre et environ 1.6x la largeur
        // C'est ce qui cible uniquement les deux cartes à côté du centre.
        const isNeighbor = !isCenter && (dist < (cardWidth * 1.6));

        // --- CALCULS D'ANIMATION (SCALE / OPACITY) ---
        const range = cardWidth * 2; 
        let t = 1 - Math.min(1, dist / range); // 1 = Centre, 0 = Loin
        
        // Scale : Centre (1.3) -> Voisin (0.9) -> Loin (0.7)
        const scale = 0.7 + (0.6 * t); 
        
        // Opacité : Centre (1) -> Loin (0.5)
        const opacity = 0.5 + (0.5 * t);

        card.style.setProperty("--scale", scale.toFixed(3));
        card.style.setProperty("--alpha", opacity.toFixed(3));
        
        // Z-Index pour que le centre passe devant
        card.style.zIndex = Math.round(t * 100);

        // --- APPLICATION DES CLASSES ---
        
        // Reset complet d'abord
        inner.classList.remove("is-active-center", "is-box");

        if (isCenter) {
            // Cas 1 : CENTRE (Pas de box, Logo géant)
            inner.classList.add("is-active-center");
        } 
        else if (isNeighbor) {
            // Cas 2 : VOISIN IMMÉDIAT (Box blanche active)
            inner.classList.add("is-box");
        } 
        // Cas 3 (impliicite) : LOIN (Pas de classe = pas de fond blanc)
      });

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [cardWidth]);

  return (
    <div
      className="ls"
      style={{
        "--speed": `${speed}s`,
        "--card-w": `${cardWidth}px`,
      }}
    >
      {showArrows && (
        <button
          className="ls__arrow ls__arrow--left"
          onClick={() => scrollByCards(-1)}
        >
          ‹
        </button>
      )}

      <div className="ls__viewport" ref={viewportRef}>
        <div
          className="ls__track"
          ref={trackRef}
          onMouseEnter={(e) => e.currentTarget.classList.add("is-paused")}
          onMouseLeave={(e) => e.currentTarget.classList.remove("is-paused")}
        >
          {doubled.map((logo, i) => (
            <div className="ls__card" key={i}>
              <div className="ls__inner">
                <img src={logo.src} alt={logo.alt || ""} />
              </div>
            </div>
          ))}
        </div>
        
        <div className="ls__fade ls__fade--l"></div>
        <div className="ls__fade ls__fade--r"></div>
      </div>

      {showArrows && (
        <button
          className="ls__arrow ls__arrow--right"
          onClick={() => scrollByCards(1)}
        >
          ›
        </button>
      )}
    </div>
  );
}